/**
 * Entry point of the statically hosted build (`npm run build:standalone`).
 *
 * The same React app the OpenComponents entry mounts, but as the whole page of
 * a site on Azure Static Web Apps. Evo loads that page in a Native Tab iframe,
 * where `EvoIframeMfesProvider` bridges environment, geography, user and access
 * token from the parent host over postMessage — so there is no OIDC login here,
 * and `useBackend()` still goes through the Evo BFF Proxy.
 *
 * Opened directly in a browser tab there is no host to ask, so the app is
 * mounted through `EvoMfesProvider` instead, with the context taken from build
 * variables and an access token supplied in the URL. That is a development aid;
 * production traffic always arrives inside the Evo iframe.
 */
import { useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { EvoIframeMfesProvider, EvoMfesProvider } from '@access-evo/mfe-react-host';
import { ChatobiApp, createI18nInstance } from '../app-tree';
import { applyHostParameters, evoUserFromToken, installButtonTokenShim } from './host-parameters';
import {
  isStandalone,
  readStandaloneApiBaseUrl,
  readStandaloneToken,
  readUrlContext,
} from './url-context';
import type { Environment, Geography } from '@access-evo/mfe-react-host';
import '../styles.css';

// Only used standalone; inside the Evo iframe these come from the host.
const ENVIRONMENT = (import.meta.env.VITE_EVO_ENVIRONMENT ?? 'preprod') as Environment;
const GEOGRAPHY = (import.meta.env.VITE_EVO_GEOGRAPHY ?? 'uk') as Geography;

type MountedAppProps = {
  i18n: Awaited<ReturnType<typeof createI18nInstance>>;
  instanceId: string;
  parameters: Record<string, string>;
  backendName?: string;
  baseUrl?: string;
};

/**
 * Rendered only once the enclosing provider has initialised the host, which is
 * what `applyHostParameters` writes to and what `useParams()` reads.
 */
function MountedApp({ i18n, instanceId, parameters, backendName, baseUrl }: MountedAppProps) {
  useMemo(() => applyHostParameters({ ...parameters, instanceId }), [parameters, instanceId]);

  return (
    <ChatobiApp
      i18n={i18n}
      instanceId={instanceId}
      shadowRoot={null}
      backendName={backendName}
      baseUrl={baseUrl}
    />
  );
}

/** Shown standalone when no token has been supplied yet. */
function TokenNeeded() {
  return (
    <main style={{ fontFamily: 'system-ui', margin: '3rem auto', maxWidth: '40rem' }}>
      <h1>ChatOBI admin — standalone mode</h1>
      <p>
        This page normally runs inside an Evo Native Tab, which supplies the signed-in user and
        access token. To run it outside Evo, append an Access Identity access token to the URL:
      </p>
      <pre>?access_token=&lt;token&gt;</pre>
      <p>
        The token needs the ChatOBI API scope and is kept in this tab&apos;s session storage only.
        Add <code>&amp;api_base_url=&lt;ChatOBI API base URL&gt;</code> to call the API directly
        instead of through the Evo BFF Proxy, and{' '}
        <code>&amp;propertyId=&lt;id&gt;</code> (or <code>groupId</code>) to choose what to
        configure.
      </p>
    </main>
  );
}

function StaticHost({ i18n }: { i18n: MountedAppProps['i18n'] }) {
  const { instanceId, parameters } = readUrlContext();

  if (!isStandalone()) {
    return (
      <EvoIframeMfesProvider>
        <MountedApp i18n={i18n} instanceId={instanceId} parameters={parameters} />
      </EvoIframeMfesProvider>
    );
  }

  const token = readStandaloneToken();
  if (!token) return <TokenNeeded />;

  installButtonTokenShim(token);
  const apiBaseUrl = readStandaloneApiBaseUrl();

  return (
    <EvoMfesProvider
      hostName="chatobi-admin-host"
      environment={ENVIRONMENT}
      geography={GEOGRAPHY}
      getEvoUser={() => Promise.resolve(evoUserFromToken(token))}
      getAccessToken={() => Promise.resolve(token)}
      parameters={parameters}
    >
      <MountedApp
        i18n={i18n}
        instanceId={instanceId}
        parameters={parameters}
        // The BFF Proxy will not accept a session it did not create, so
        // standalone the API is called directly with the supplied token.
        backendName={apiBaseUrl ? 'evo-oidc' : undefined}
        baseUrl={apiBaseUrl ?? undefined}
      />
    </EvoMfesProvider>
  );
}

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');

createI18nInstance('/resources.json').then((i18n) => {
  createRoot(container).render(<StaticHost i18n={i18n} />);
});
